import React, { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { DurationIcon, LightbulbIcon, CheckCircleFilledIcon } from '@/src/renderer/components/icons'
import { ShortcutHint } from '@/src/renderer/components/ui/ShortcutHint'
import { Button } from '@/src/renderer/components/ui/Button'
import { parseDuration } from '../../utils/parseDuration'
import type { DurationValue } from '../TaskEntry'
import type { DurationDropdownContentProps } from './DurationDropdown.types'
import './DurationDropdown.scss'

const MINUTE_OPTIONS: { minutes: number; labelKey: string }[] = [
  { minutes: 5, labelKey: 'taskCreation.duration5min' },
  { minutes: 10, labelKey: 'taskCreation.duration10min' },
  { minutes: 15, labelKey: 'taskCreation.duration15min' },
  { minutes: 30, labelKey: 'taskCreation.duration30min' },
  { minutes: 45, labelKey: 'taskCreation.duration45min' },
]

const HOUR_OPTIONS: { minutes: number; labelKey: string }[] = [
  { minutes: 60, labelKey: 'taskCreation.duration1h' },
  { minutes: 120, labelKey: 'taskCreation.duration2h' },
  { minutes: 180, labelKey: 'taskCreation.duration3h' },
  { minutes: 240, labelKey: 'taskCreation.durationHalfDay' },
  { minutes: 480, labelKey: 'taskCreation.durationFullDay' },
]

const THINKING_MINUTES = 10

/**
 * Content for the duration dropdown menu.
 * Renders inside a DropdownMenuContent managed by TaskActionButton.
 *
 * Features:
 * - Search/filter input with natural language parsing (10m, 2h, 1:30)
 * - Two-column layout: Minutes (1-5) and Hours (6-8)
 * - "10 Min zum Durchdenken" special option (T)
 * - Keyboard shortcut T for thinking mode
 */
export const DurationDropdownContent: React.FC<DurationDropdownContentProps> = ({ value, onChange, onClose, onRemove, variant = 'actionbar' }) => {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const handleSelect = useCallback((val: DurationValue) => {
    onChange(val)
    onClose()
  }, [onChange, onClose])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Always stop propagation so Radix typeahead/navigation doesn't steal focus.
    e.stopPropagation()

    if (e.key === 'Enter') {
      e.preventDefault()
      const parsed = parseDuration(inputValue)
      if (parsed !== null && parsed > 0) {
        handleSelect({ minutes: parsed, mode: 'duration' })
      }
      return
    }

    if (e.key === 't' || e.key === 'T') {
      // Only trigger thinking shortcut when input is empty.
      if (inputValue === '') {
        e.preventDefault()
        handleSelect({ minutes: THINKING_MINUTES, mode: 'thinking' })
      }
    }
  }, [handleSelect, inputValue])

  const isSelected = (minutes: number, mode: 'duration' | 'thinking') =>
    value?.minutes === minutes && value?.mode === mode

  return (
    <div className="duration-dropdown" data-variant={variant}>
      <div className="duration-dropdown__search-wrapper">
        <div className="duration-dropdown__search">
          <DurationIcon size={16} />
          <input
            ref={inputRef}
            className="duration-dropdown__input"
            placeholder={t('taskCreation.durationPlaceholder')}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        </div>
      </div>

      <div className="duration-dropdown__selection">
        <div className="duration-dropdown__columns">
          {/* Minutes column */}
          <div className="duration-dropdown__column">
            <div className="duration-dropdown__column-label">
              {t('taskCreation.durationMinutes')}
            </div>
            {MINUTE_OPTIONS.map(option => (
              <button
                key={option.minutes}
                type="button"
                className={`context-menu-content__item duration-dropdown__item${isSelected(option.minutes, 'duration') ? ' duration-dropdown__item--selected' : ''}`}
                onClick={() => handleSelect({ minutes: option.minutes, mode: 'duration' })}
              >
                {t(option.labelKey)}
                {isSelected(option.minutes, 'duration') && (
                  <div className="duration-dropdown__item-right">
                    <CheckCircleFilledIcon size={16} className="duration-dropdown__check" />
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Vertical separator */}
          <div className="duration-dropdown__separator-vertical" />

          {/* Hours column */}
          <div className="duration-dropdown__column">
            <div className="duration-dropdown__column-label">
              {t('taskCreation.durationHours')}
            </div>
            {HOUR_OPTIONS.map(option => (
              <button
                key={option.minutes}
                type="button"
                className={`context-menu-content__item duration-dropdown__item${isSelected(option.minutes, 'duration') ? ' duration-dropdown__item--selected' : ''}`}
                onClick={() => handleSelect({ minutes: option.minutes, mode: 'duration' })}
              >
                {t(option.labelKey)}
                {isSelected(option.minutes, 'duration') && (
                  <div className="duration-dropdown__item-right">
                    <CheckCircleFilledIcon size={16} className="duration-dropdown__check" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="context-menu-content__separator" />

        {/* Thinking option */}
        <button
          type="button"
          className={`context-menu-content__item duration-dropdown__item duration-dropdown__item--thinking${isSelected(THINKING_MINUTES, 'thinking') ? ' duration-dropdown__item--selected' : ''}`}
          onClick={() => handleSelect({ minutes: THINKING_MINUTES, mode: 'thinking' })}
        >
          <LightbulbIcon size={16} />
          {t('taskCreation.durationThinking')}
          <div className="duration-dropdown__item-right">
            {isSelected(THINKING_MINUTES, 'thinking') && (
              <CheckCircleFilledIcon size={16} className="duration-dropdown__check" />
            )}
            <ShortcutHint>T</ShortcutHint>
          </div>
        </button>
      </div>

      {variant === 'badge' && onRemove && (
        <div className="duration-dropdown__remove-section">
          <div className="context-menu-content__separator" />
          <div className="duration-dropdown__remove-wrapper">
            <Button variant="outline" icon={<Trash2 size={16} />} onClick={onRemove}>
              {t('taskCreation.durationRemove')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
